import {
  formatDate as sharedFormatDate,
  formatNumber,
  formatPct as sharedFormatPct,
  todayIso,
  toIsoDate,
  diffDays,
  toHourly,
  type EnumDef,
  type IsoDate,
  type RateUnit,
} from '@clawhire/shared';

/**
 * Display helpers. All dates are shown as DD.MM.YYYY, rates as "60 EUR/h", percentages as "20 %".
 * Everything tolerates null/undefined and returns an en dash placeholder.
 */

export const EMPTY = '–';

/** YYYY-MM-DD (or ISO datetime / Date) -> DD.MM.YYYY; '' for empty, EMPTY for garbage. */
export function formatDate(value: Date | string | null | undefined, fallback: string = ''): string {
  if (value === null || value === undefined || value === '') return fallback;
  try {
    return sharedFormatDate(value) || fallback;
  } catch {
    return fallback;
  }
}

/** ISO datetime -> "DD.MM.YYYY HH:mm" in the browser's time zone. */
export function formatDateTime(value: string | Date | null | undefined, fallback: string = ''): string {
  if (!value) return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
}

/** "today", "tomorrow", "in 3 days", "yesterday", "5 days ago". */
export function formatRelativeDays(value: string | null | undefined, today: IsoDate = todayIso()): string {
  if (!value) return '';
  let iso: IsoDate | null;
  try {
    iso = toIsoDate(value);
  } catch {
    return '';
  }
  if (!iso) return '';
  const diff = diffDays(today, iso);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  return diff > 0 ? `in ${diff} days` : `${-diff} days ago`;
}

/** Rate with unit: 60 -> "60 EUR/h", (480, 'DAY') -> "480 EUR/day". */
export function formatRate(
  rate: number | null | undefined,
  unit: RateUnit | null | undefined = 'HOUR',
): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return EMPTY;
  return `${formatNumber(rate)} EUR/${unit === 'DAY' ? 'day' : 'h'}`;
}

/** Day rate shown with its hourly equivalent: "480 EUR/day (60 EUR/h)". Hourly rates unchanged. */
export function formatRateWithHourly(
  rate: number | null | undefined,
  unit: RateUnit | null | undefined = 'HOUR',
  hoursPerDay = 8,
): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return EMPTY;
  if (unit === 'DAY')
    return `${formatRate(rate, 'DAY')} (${formatRate(toHourly(rate, 'DAY', hoursPerDay), 'HOUR')})`;
  return formatRate(rate, 'HOUR');
}

/** Plain EUR amount: 1234.5 -> "1234.5 EUR". */
export function formatEur(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return EMPTY;
  return `${formatNumber(amount)} EUR`;
}

/** 20 -> "20 %", 12.5 -> "12.5 %". */
export function formatPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return EMPTY;
  return `${sharedFormatPct(pct)} %`;
}

/** Number with thousands separators (de-DE style dots would clash with dates; we use narrow spaces). */
export function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return EMPTY;
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return EMPTY;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Human label for an enum code; unknown codes are shown as-is (never crash on new values). */
export function enumLabel<T extends string>(def: EnumDef<T>, code: T | string | null | undefined): string {
  if (!code) return EMPTY;
  return def.is(code) ? def.label(code) : String(code);
}

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

/** `{ value, label }[]` for selects, in the order the enum defines. */
export function enumOptions<T extends string>(def: EnumDef<T>, only?: readonly T[]): Option<T>[] {
  const codes = only ?? def.codes;
  return codes.map((code) => ({ value: code, label: def.label(code) }));
}

/** Boolean -> "Yes" / "No" (EMPTY for null). */
export function formatBool(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return EMPTY;
  return value ? 'Yes' : 'No';
}

/** Join list values for table cells: ["SAP", "ABAP"] -> "SAP, ABAP". */
export function formatList(values: readonly string[] | null | undefined, max = 3): string {
  if (!values || values.length === 0) return EMPTY;
  const shown = values.slice(0, max).join(', ');
  return values.length > max ? `${shown} +${values.length - max}` : shown;
}

/** Truncate long text for cells and cards. */
export function truncate(text: string | null | undefined, max = 80): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}
